/**
 * 问卷数据转换层
 * 将后端数据模型转换为前端渲染模型
 */

/**
 * 通用复合题识别配置
 */
const COMPOSITE_RULES = {
  LIKERT: {
    // 识别条件：题目类型匹配
    typeMatcher: (q) => q.type === 'LIKERT_SCALE' && q.parentQuestionId,
    // 子题筛选：查找所有共享同一 parentQuestionId 的子题
    siblingFilter: (q, allQuestions) => {
      return allQuestions.filter(
        item => item.parentQuestionId === q.parentQuestionId && 
                item.type === 'LIKERT_SCALE'
      );
    },
    // 配置一致性检查：所有子题的 scaleConfig 必须完全一致
    configMatcher: (baseQuestion, sibling) => {
      if (!sibling.likertConfig || !baseQuestion.likertConfig) return false;
      return (
        sibling.likertConfig.leftLabel === baseQuestion.likertConfig.leftLabel &&
        sibling.likertConfig.rightLabel === baseQuestion.likertConfig.rightLabel &&
        JSON.stringify(sibling.likertConfig.options) === JSON.stringify(baseQuestion.likertConfig.options)
      );
    },
    // 父题存在性检查
    parentExists: (q, allQuestions) => {
      return allQuestions.some(item => item.id === q.parentQuestionId);
    },
    // 最小子题数量（允许1个子题，如第40题消极感受）
    minSiblings: 1
  },
  PAIN_RATING: {
    // 识别条件：SINGLE_CHOICE（是/否）+ 紧跟着的 RATING
    typeMatcher: (q, allQuestions, currentIndex) => {
      if (q.type !== 'SINGLE_CHOICE') {
        return false;
      }
      
      // 检查选项是否为"是/否"
      const hasYesNoOptions = q.options && 
        q.options.length === 2 &&
        q.options.some(opt => opt.value === 'yes' || opt.label === '是') &&
        q.options.some(opt => opt.value === 'no' || opt.label === '否');
      
      if (!hasYesNoOptions) {
        return false;
      }
      
      // 检查下一题是否为 RATING 类型
      const nextQuestion = allQuestions[currentIndex + 1];
      if (!nextQuestion || nextQuestion.type !== 'RATING') {
        return false;
      }
      
      // 检查 RATING 题是否包含疼痛相关关键词
      const ratingTitle = nextQuestion.title || '';
      const painKeywords = ['疼痛', '疼痛程度', '打分', '评分'];
      const hasPainKeyword = painKeywords.some(keyword => ratingTitle.includes(keyword));
      
      return hasPainKeyword;
    },
    // 子题筛选：返回 [是否题, 评分题]
    siblingFilter: (q, allQuestions, currentIndex) => {
      const nextQuestion = allQuestions[currentIndex + 1];
      return nextQuestion ? [q, nextQuestion] : [];
    },
    // 配置一致性检查：疼痛评分题不需要检查配置一致性（固定模式）
    configMatcher: () => true,
    // 父题存在性检查：疼痛评分题不需要父题
    parentExists: () => true,
    // 最小子题数量：固定为2（是否题 + 评分题）
    minSiblings: 2
  }
};

/**
 * 通用复合题有效性判断（抽象自 Likert 逻辑）
 * @param {Object} question - 当前题目
 * @param {Array} allQuestions - 所有题目
 * @param {number} currentIndex - 当前题目索引
 * @param {string} compositeType - 复合题类型（'LIKERT' | 'PAIN_RATING'）
 * @returns {boolean} 是否有效
 */
function isValidComposite(question, allQuestions, currentIndex, compositeType) {
  const rule = COMPOSITE_RULES[compositeType];
  if (!rule) {
    return false;
  }
  
  // 1. 类型匹配检查
  const typeMatch = typeof rule.typeMatcher === 'function' && rule.typeMatcher.length === 3
    ? rule.typeMatcher(question, allQuestions, currentIndex)
    : rule.typeMatcher(question);
  
  if (!typeMatch) return false;
  
  // 2. 查找子题
  const siblings = typeof rule.siblingFilter === 'function' && rule.siblingFilter.length === 3
    ? rule.siblingFilter(question, allQuestions, currentIndex)
    : rule.siblingFilter(question, allQuestions);
  
  if (siblings.length < rule.minSiblings) {
    return false;
  }
  
  // 3. 配置一致性检查（如果适用）
  if (compositeType === 'LIKERT') {
    const allConfigsMatch = siblings.every(sibling => 
      rule.configMatcher(question, sibling)
    );
    if (!allConfigsMatch) return false;
  }
  
  // 4. 父题存在性检查（如果适用）
  if (compositeType === 'LIKERT') {
    const parentExists = rule.parentExists(question, allQuestions);
    if (!parentExists) return false;
  }
  
  return true;
}

/**
 * 构建 Likert 复合题
 */
function buildLikertComposite(firstSubQuestion, allQuestions) {
  const parentId = firstSubQuestion.parentQuestionId;
  
  // 收集所有子问题
  const allSubQuestions = allQuestions.filter(q => q.parentQuestionId === parentId && q.type === 'LIKERT_SCALE');
  
  const subQuestions = allSubQuestions
    .sort((a, b) => {
      // 按题目ID排序，保证顺序一致
      const aNum = parseInt(a.id.replace('q', ''));
      const bNum = parseInt(b.id.replace('q', ''));
      return aNum - bNum;
    })
    .map(q => ({
      id: q.id,
      title: q.title,
      required: q.required
    }));
  
  return {
    scaleConfig: firstSubQuestion.likertConfig,
    subQuestions: subQuestions
  };
}

/**
 * 获取父题标题
 * TODO: 这是临时方案，后续需要通过虚拟父题或后端字段解决
 * 注意：parentId 实际上就是第一个子题的 ID，所以不能直接查找 allQuestions
 * 应该直接使用硬编码映射
 */
function getParentTitle(parentId, allQuestions) {
  // 查找虚拟父题记录（但需要排除 Likert 子题，因为 parentId 就是子题 ID）
  const parentQuestion = allQuestions.find(q => q.id === parentId && !q.parentQuestionId);
  
  if (parentQuestion && parentQuestion.title) {
    return parentQuestion.title;
  }
  
  // TODO: 临时硬编码映射，后续需改为虚拟父题或后端字段
  // 直接使用配置映射（因为 parentId 就是子题 ID，不能使用子题标题）
  // 更新后的父题ID映射（因插入新题13-20，原q36-q40改为q44-q48，原q48改为q56）
  const parentTitleMap = {
    'q44': '请选择下列问题中最能反映您近一个月来实际情况的选项',  // 原q36
    'q45': '请选择下列问题中最能反映您近一个月来实际情况的选项',  // 原q37
    'q46': '请选择下列问题中最能反映您近一个月来实际情况的选项',  // 原q38
    'q47': '请选择下列问题中最能反映您近一个月来实际情况的选项',  // 原q39
    'q48': '请选择下列问题中最能反映您近一个月来实际情况的选项',  // 原q40
    'q56': '请选择下列问题中最能反映您近半年来实际情况的选项'   // 原q48
  };
  
  return parentTitleMap[parentId] || `题目 ${parentId}`;
}

/**
 * 构建疼痛评分复合题
 * 复用通用复合题识别逻辑
 */
function buildPainRatingComposite(yesNoQuestion, allQuestions, currentIndex) {
  const ratingQuestion = allQuestions[currentIndex + 1];
  
  // 从是否题的标题中提取部位名称（如"颈部"）
  // 移除「（ ）」前缀、「过去一年里，」和「发生过疼痛或不适（酸、麻、胀等）」后缀
  let title = yesNoQuestion.title
    .replace(/^（\s*）\s*/, '')  // 移除中文括号前缀
    .replace(/^\(\s*\)\s*/, '')  // 移除英文括号前缀
    .replace('过去一年里，', '')
    .replace('发生过疼痛或不适（酸、麻、胀等）', '')
    .replace('发生过疼痛或不适（酸、麻、胀等)', '')  // 处理肩部的英文右括号
    .trim();
  
  return {
    title: title,
    yesNoQuestion: {
      id: yesNoQuestion.id,
      title: yesNoQuestion.title,
      required: yesNoQuestion.required
    },
    ratingQuestion: {
      id: ratingQuestion.id,
      title: ratingQuestion.title,
      required: ratingQuestion.required
    },
    ratingConfig: ratingQuestion.ratingConfig
  };
}

/**
 * 转换基础题型
 */
function transformBasicQuestion(question, displayIndex) {
  return {
    id: question.id,
    displayIndex: displayIndex,
    title: question.title,
    type: question.type,
    required: question.required,
    options: question.options,
    numberConfig: question.numberConfig,
    ratingConfig: question.ratingConfig,
    likertConfig: question.likertConfig,
    matrixConfig: question.matrixConfig,
    skipLogic: question.skipLogic || null  // 保留跳转逻辑信息
  };
}

/**
 * 标记所有子问题已处理
 * 使用 id + type 作为唯一标识，避免同一ID但不同类型题目被误跳过
 */
function markSubQuestionsProcessed(parentId, allQuestions, processedIds) {
  allQuestions
    .filter(q => q.parentQuestionId === parentId)
    .forEach(q => processedIds.add(`${q.id}:${q.type}`));
}

/**
 * 生成题目的唯一标识（id + type）
 */
function getQuestionKey(question) {
  return `${question.id}:${question.type}`;
}

/**
 * 后端数据 → 前端渲染模型的转换函数
 */
export function transformBackendToView(backendQuestions) {
  const viewQuestions = [];
  const processedIds = new Set();
  let displayIndex = 1;
  
  // 确保后端题目按 id 排序（q1, q2, q3...），保证顺序一致
  // 如果ID相同，按类型排序（确保处理顺序一致）
  const sortedQuestions = [...backendQuestions].sort((a, b) => {
    const aNum = parseInt(a.id.replace('q', '')) || 0;
    const bNum = parseInt(b.id.replace('q', '')) || 0;
    if (aNum !== bNum) {
      return aNum - bNum;
    }
    // ID相同时，按类型排序（确保处理顺序一致）
    return (a.type || '').localeCompare(b.type || '');
  });
  
  for (let i = 0; i < sortedQuestions.length; i++) {
    const q = sortedQuestions[i];
    const questionKey = getQuestionKey(q);
    
    // 跳过已处理的题目（作为子问题已合并）
    // 使用 id + type 作为唯一标识，避免同一ID但不同类型题目被误跳过
    if (processedIds.has(questionKey)) {
      continue;
    }
    
    // 跳过父题（如果它有子题，子题已经被处理为复合题）
    // 检查是否有子题使用此题目作为parentQuestionId
    if (q.type === 'LIKERT_SCALE' && !q.parentQuestionId) {
      const subQuestions = sortedQuestions.filter(subQ => 
        subQ.type === 'LIKERT_SCALE' && subQ.parentQuestionId === q.id
      );
      const hasSubQuestions = subQuestions.length > 0;
      
      if (hasSubQuestions) {
        // 这是父题，有子题，应该跳过（子题已经被处理为复合题）
        continue;
      }
    }
    
    // 情况1：Likert量表题（使用通用复合题识别逻辑）
    if (q.type === 'LIKERT_SCALE' && q.parentQuestionId) {
      if (isValidComposite(q, sortedQuestions, i, 'LIKERT')) {
        const composite = buildLikertComposite(q, sortedQuestions);
        const parentId = q.parentQuestionId;
        
        // 使用递增的displayIndex（所有题目统一从1开始连续编号）
        const assignedDisplayIndex = displayIndex++;
        
        viewQuestions.push({
          id: parentId,
          displayIndex: assignedDisplayIndex,
          title: getParentTitle(parentId, sortedQuestions),  // TODO: 临时硬编码，需改为虚拟父题
          type: 'LIKERT_COMPOSITE',
          required: true,
          compositeConfig: {
            compositeType: 'LIKERT',
            schemaVersion: '1.0',
            layout: 'GRID',
            likert: composite
          }
        });
        // 标记所有子问题已处理
        markSubQuestionsProcessed(parentId, sortedQuestions, processedIds);
        continue;
      }
      // 如果不满足合并条件，按普通Likert题处理（降级）
    }
    
    // 情况2：疼痛评分题（保持复合题结构，但占用2个 displayIndex）
    if (isValidComposite(q, sortedQuestions, i, 'PAIN_RATING')) {
      const composite = buildPainRatingComposite(q, sortedQuestions, i);
      const nextQuestion = sortedQuestions[i + 1];
      
      // 是否题：占用第一个 displayIndex
      const yesNoDisplayIndex = displayIndex++;
      viewQuestions.push({
        id: q.id,
        displayIndex: yesNoDisplayIndex,
        title: q.title,
        type: 'SINGLE_CHOICE',
        required: q.required,
        options: q.options,
        painRatingRelated: {
          isYesNoQuestion: true,
          ratingQuestionId: nextQuestion.id
        }
      });
      
      // 评分题：占用第二个 displayIndex，但标记为疼痛评分题，需要关联是否题
      const ratingDisplayIndex = displayIndex++;
      viewQuestions.push({
        id: nextQuestion.id,
        displayIndex: ratingDisplayIndex,
        title: nextQuestion.title,
        type: 'RATING',
        required: false,  // 如果选"否"，可以不填
        ratingConfig: nextQuestion.ratingConfig,
        painRatingRelated: {
          isRatingQuestion: true,
          yesNoQuestionId: q.id
        }
      });
      
      processedIds.add(getQuestionKey(q));
      processedIds.add(getQuestionKey(nextQuestion));
      i++; // 跳过下一题（评分题）
      continue;
    }
    
    // 情况3：矩阵题（保持原样，直接使用 matrixConfig）
    if (q.type === 'MATRIX') {
      const assignedDisplayIndex = displayIndex++;
      viewQuestions.push({
        id: q.id,
        displayIndex: assignedDisplayIndex,
        title: q.title,
        type: 'MATRIX',
        required: q.required,
        matrixConfig: q.matrixConfig  // 直接使用 matrixConfig，而不是放在 compositeConfig 中
      });
      continue;
    }
    
    // 情况4：基础题型（单选、多选、文本、数值、普通Likert）
    const assignedDisplayIndex = displayIndex++;
    viewQuestions.push(transformBasicQuestion(q, assignedDisplayIndex));
  }
  
  // 重新分配displayIndex，确保所有题目从1开始连续编号
  // 按 displayIndex 排序，然后重新分配连续的序号
  viewQuestions.sort((a, b) => {
    return (a.displayIndex || 0) - (b.displayIndex || 0);
  });
  
  // 从1开始重新分配连续的displayIndex
  viewQuestions.forEach((q, index) => {
    q.displayIndex = index + 1;
  });
  
  return viewQuestions;
}

/**
 * 根据答案和跳转逻辑计算可见题目列表
 * @param {Array} allQuestions - 所有题目（已转换的前端模型）
 * @param {Object} answers - 答案对象 {questionId: answer}
 * @returns {Array} 可见题目列表
 */
export function calculateVisibleQuestions(allQuestions, answers) {
  if (!allQuestions || allQuestions.length === 0) {
    return [];
  }
  
  // 按 displayIndex 排序
  const sortedQuestions = [...allQuestions].sort((a, b) => {
    return (a.displayIndex || 0) - (b.displayIndex || 0);
  });
  
  const visibleQuestionIds = new Set();
  const questionMap = new Map();
  sortedQuestions.forEach(q => {
    questionMap.set(q.id, q);
  });
  
  // 从第一题开始，按顺序处理
  let i = 0;
  while (i < sortedQuestions.length) {
    const currentQuestion = sortedQuestions[i];
    
    // 第一题总是可见
    if (i === 0) {
      visibleQuestionIds.add(currentQuestion.id);
    }
    
    // 检查当前题目是否可见（被前面的题目跳转逻辑影响）
    if (!visibleQuestionIds.has(currentQuestion.id) && i > 0) {
      i++;
      continue;
    }
    
    // 检查当前题目的答案，判断是否需要跳转
    const answer = answers[currentQuestion.id];
    const skipLogic = currentQuestion.skipLogic;
    
    if (skipLogic && answer !== null && answer !== undefined) {
      // 检查是否满足跳转条件
      let shouldSkip = false;
      const answerValue = String(answer);
      
      if (skipLogic.condition === 'EQUALS') {
        shouldSkip = answerValue === skipLogic.conditionValue;
      } else if (skipLogic.condition === 'NOT_EQUALS') {
        shouldSkip = answerValue !== skipLogic.conditionValue;
      }
      
      if (shouldSkip) {
        // 需要跳转
        if (skipLogic.targetQuestionId) {
          // 跳转到指定题目
          const targetIndex = sortedQuestions.findIndex(q => q.id === skipLogic.targetQuestionId);
          if (targetIndex > -1) {
            // 标记目标题目可见
            visibleQuestionIds.add(skipLogic.targetQuestionId);
            // 跳过当前题目和下一题之间的所有题目（不添加到可见列表）
            // 继续从目标题目开始处理
            i = targetIndex;
            continue;
          }
        } else {
          // 跳过下一题（targetQuestionId 为 null）
          if (i + 1 < sortedQuestions.length) {
            // 下一题不可见，跳过
            i += 2; // 跳过下一题
            continue;
          }
        }
      }
    }
    
    // 如果当前题目可见，下一题也可见（除非被跳转逻辑覆盖）
    if (visibleQuestionIds.has(currentQuestion.id)) {
      // 检查是否有跳转逻辑阻止下一题显示
      const answer = answers[currentQuestion.id];
      const skipLogic = currentQuestion.skipLogic;
      let shouldSkipNext = false;
      
      if (skipLogic && answer !== null && answer !== undefined) {
        const answerValue = String(answer);
        if (skipLogic.condition === 'EQUALS') {
          shouldSkipNext = answerValue === skipLogic.conditionValue;
        } else if (skipLogic.condition === 'NOT_EQUALS') {
          shouldSkipNext = answerValue !== skipLogic.conditionValue;
        }
      }
      
      if (!shouldSkipNext && i + 1 < sortedQuestions.length) {
        // 没有跳转逻辑，或者不满足跳转条件，下一题可见
        const nextQuestion = sortedQuestions[i + 1];
        visibleQuestionIds.add(nextQuestion.id);
      }
    }
    
    i++;
  }
  
  // 返回可见题目列表，按 displayIndex 排序
  return sortedQuestions.filter(q => visibleQuestionIds.has(q.id));
}
